import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import { PropId } from "building-types/props/propIds";
import type { AwningSettings } from "building-types/settings/awningSettings";
import type { ExteriorPropSettings } from "building-types/settings/exteriorPropSettings";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { PorchSettings } from "building-types/settings/porchSettings";
import { SettingsType } from "building-types/settings/settingsType";
import { AwningType, EaveType, FasciaTopTrimSize, ShutterType } from "building-types/settings/settingsTypesEnum";
import { defaultExteriorPropCatalog } from "../elements/propCatalog/exteriorPropCatalog";
import { fasciaTopTrimSizeToValue } from "../fasciaTopTrimSizeToValue";
import { headCasingSlicesNew } from "../settingsTools";
import { BuiltinHomeSettingsId, builtinHomeSettingsPrettyName } from "./builtinHomeSettingsId";
import {
  defaultAwningSettings,
  defaultExteriorDoorCasingSettings,
  defaultFarmhouseWindowSettings,
  defaultFriezeBoardSettings,
  defaultHomeSettings,
  defaultPorchSettings,
  defaultWainscotingSettings,
} from "./defaultSettings";

const defaultEssexPorchSettings: PorchSettings = {
  ...defaultPorchSettings,
  maxColumnSpacing: 240,
  doorPathColumnSpread: 60,
  columnId: "aatpy2_89sz2",
};

const essexAwningSettings: AwningSettings = {
  type: SettingsType.AWNING_SETTINGS,
  awningType: AwningType.SHED,
  slope: 1 / 2,
  depth: 25.5,
  fasciaHeight: 6,
  trimHeight: 2,
  trimWidth: 1,
  awningBracketPropId: BuiltInExteriorPropId.AWNINGBRACKET,
  bracketOffset: 2,
  bracketTrimSpacing: 3,
};

const essexExteriorPropSettings: ExteriorPropSettings = {
  type: SettingsType.EXTERIOR_PROP_SETTINGS,
  propTypes: {
    ...defaultExteriorPropCatalog,
    garageDoorSingle: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
      propAssetId: PropId.DoorGar_S8x7_4x4Pattern_01,
    },
    garageDoorDouble: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
      propAssetId: PropId.DoorGar_D16x7_4x4Pattern_01,
    },
    doorSingleGlass: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
      propAssetId: PropId.DoorExt_Single36_QuarterSolid_02,
    },
    doorSingleGlassLow: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
      propAssetId: PropId.DoorExt_Single36_QuarterSolid_01,
    },
  },
};

const essexMaterials: { [id: string]: HomeSettings } = {
  [BuiltinHomeSettingsId.Essex_W1A]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Essex_W1A],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Essex_W1A + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Essex_W1A],
    },
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 14,
      rakeOverhang: 14,
      shedOverhang: 8,
      eaveType: EaveType.BOXED_BOX,
      rakeBracketParams: [36],
      rakeBracketAtPeak: true,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 0,
      wainscotingMode: StreetOrientationMode.FRONT_FACES,
    },
    windowShutters: {
      type: SettingsType.WINDOW_SHUTTER_SETTINGS,
      shutterType: ShutterType.SIMPLE_SLATS,
    },
    exteriorDoorCasing: {
      ...defaultExteriorDoorCasingSettings,
      ...headCasingSlicesNew(
        [
          { height: 6, depthOffset: 0.2, widthOffset: 0 },
          { height: 1, depthOffset: 0.5, widthOffset: 1 },
          { height: 1, depthOffset: 0.8, widthOffset: 1 },
        ],
        true,
        10,
        2,
        4,
        4,
      ),
    },
    awning: { ...defaultAwningSettings, type: SettingsType.AWNING_SETTINGS, awningType: AwningType.DISABLED },
    porch: defaultEssexPorchSettings,
    exteriorProps: essexExteriorPropSettings,
  },
  [BuiltinHomeSettingsId.Essex_W2A]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Essex_W2A],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Essex_W2A + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Essex_W2A],
    },
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 14,
      rakeOverhang: 14,
      shedOverhang: 8,
      eaveType: EaveType.BOXED_MINI_HIP,
      rakeBracketPropId: BuiltInExteriorPropId.RAKEBRACKET,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 20,
      wainscotingMode: StreetOrientationMode.FRONT_FACES,
      closeDist: 0,
    },

    windowShutters: {
      type: SettingsType.WINDOW_SHUTTER_SETTINGS,
      shutterType: ShutterType.DISABLED,
    },

    awning: essexAwningSettings,
    porch: defaultEssexPorchSettings,
    exteriorProps: essexExteriorPropSettings,
  },
  [BuiltinHomeSettingsId.Essex_W2D]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Essex_W2D],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Essex_W2D + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Essex_W2D],
    },
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 14,
      rakeOverhang: 14,
      shedOverhang: 8,
      eaveType: EaveType.BOXED_BOX,
      rakeBracketPropId: BuiltInExteriorPropId.RAKEBRACKET,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 20,
      wainscotingMode: StreetOrientationMode.FRONT_FACES,
      closeDist: 0,
    },

    ...defaultFarmhouseWindowSettings,

    awning: { ...defaultAwningSettings, type: SettingsType.AWNING_SETTINGS, awningType: AwningType.DISABLED },
    porch: defaultEssexPorchSettings,

    exteriorProps: essexExteriorPropSettings,
  },
};

export default essexMaterials;
